'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { createClient } from '@/lib/supabase/client';

const ADMIN_ROLES = ['admin', 'super_admin', 'grand_lodge_admin'];

interface NavItem {
  label: string;
  href: string;
  icon: string;
  badge?: number;
  group?: string;
  adminOnly?: boolean;
}

const navItems: NavItem[] = [
  { label: 'Dashboard', href: '/', icon: 'HomeIcon', group: 'Main' },
  { label: 'Archive', href: '/archive-record-catalog', icon: 'BookOpenIcon', badge: 0, group: 'Main' },
  { label: 'Search', href: '/search', icon: 'MagnifyingGlassIcon', group: 'Main' },
  { label: 'Capture Record', href: '/document-capture-screen', icon: 'CameraIcon', group: 'Main' },
  { label: 'Transcription Review', href: '/document-transcription-review', icon: 'DocumentTextIcon', badge: 7, group: 'Review' },
  { label: 'Contributor Upload', href: '/contributor-upload-page', icon: 'ArrowUpTrayIcon', group: 'Review' },
  { label: 'Admin Panel', href: '/admin', icon: 'ShieldCheckIcon', group: 'Admin', adminOnly: true },
  { label: 'User Management', href: '/admin/users', icon: 'UsersIcon', group: 'Admin', adminOnly: true },
  { label: 'Activity Logs', href: '/admin/logs', icon: 'ClipboardDocumentListIcon', group: 'Admin', adminOnly: true },
  { label: 'Reports', href: '/admin/reports', icon: 'ChartBarIcon', group: 'Admin', adminOnly: true },
];

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

export default function Sidebar({ collapsed, onToggle }: SidebarProps) {
  const pathname = usePathname();
  const router = useRouter();
  const { signOut } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const [lodgeName, setLodgeName] = useState<string>('Loading…');
  const [lodgeLocation, setLodgeLocation] = useState<string>('');
  const [userRole, setUserRole] = useState<string>('member');
  const { user } = useAuth();

  useEffect(() => {
    if (!user) return;
    const supabase = createClient();
    supabase
      .from('user_profiles')
      .select('role, lodge_id, lodges(name, city, province_state)')
      .eq('auth_user_id', user.id)
      .maybeSingle()
      .then(({ data }) => {
        if (data?.role) setUserRole(data.role);
        if (data?.lodges) {
          const lodge = data.lodges as any;
          setLodgeName(lodge.name || 'Your Lodge');
          setLodgeLocation([lodge.city, lodge.province_state].filter(Boolean).join(', '));
        } else {
          setLodgeName('The Masonic Vault');
          setLodgeLocation('');
        }
      });
  }, [user]);

  const handleSignOut = async () => {
    try {
      await signOut();
      router.push('/sign-up-login-screen');
      router.refresh();
    } catch {
      router.push('/sign-up-login-screen');
    }
  };

  const isAdmin = ADMIN_ROLES.includes(userRole);
  const groups = ['Main', 'Review', ...(isAdmin ? ['Admin'] : [])];

  return (
    <aside
      className="flex flex-col h-full overflow-hidden"
      style={{
        width: collapsed ? 64 : 240,
        background: 'linear-gradient(180deg, #0D1829 0%, #060E1C 100%)',
        borderRight: '1px solid rgba(201, 168, 76, 0.2)',
        transition: 'width 300ms ease',
        flexShrink: 0,
      }}
    >
      {/* Logo */}
      <div
        className="flex items-center gap-3 px-4 py-5"
        style={{ borderBottom: '1px solid rgba(201, 168, 76, 0.2)', minHeight: 72 }}
      >
        <div className="flex-shrink-0">
          <AppLogo
            src="/assets/images/logo-1777919850828.png"
            size={36}
            onClick={onToggle}
          />
        </div>
        {!collapsed && (
          <div className="overflow-hidden">
            <span
              className="font-serif text-base font-semibold leading-tight block"
              style={{ color: '#C9A84C', whiteSpace: 'nowrap' }}
            >
              The Masonic
            </span>
            <span
              className="font-serif text-base font-semibold leading-tight block"
              style={{ color: '#F5F0E8', whiteSpace: 'nowrap' }}
            >
              Vault
            </span>
          </div>
        )}
      </div>

      {/* Lodge info */}
      {!collapsed && (
        <div
          className="px-4 py-3"
          style={{ borderBottom: '1px solid rgba(201, 168, 76, 0.15)' }}
        >
          <p className="text-xs font-semibold uppercase tracking-widest mb-1" style={{ color: 'rgba(201, 168, 76, 0.6)' }}>
            Active Lodge
          </p>
          <p className="text-sm font-semibold truncate" style={{ color: '#F5F0E8' }}>
            {lodgeName}
          </p>
          {lodgeLocation && (
            <p className="text-xs truncate" style={{ color: 'rgba(245, 240, 232, 0.5)' }}>
              {lodgeLocation}
            </p>
          )}
        </div>
      )}

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto py-4 px-2 scrollbar-thin">
        {groups.map((group) => {
          const items = navItems.filter((n) => n.group === group && (!n.adminOnly || isAdmin));
          return (
            <div key={`group-${group}`} className="mb-4">
              {!collapsed && (
                <p
                  className="text-xs font-semibold uppercase tracking-widest px-3 mb-2"
                  style={{ color: group === 'Admin' ? 'rgba(201, 168, 76, 0.8)' : 'rgba(201, 168, 76, 0.5)' }}
                >
                  {group}
                </p>
              )}
              {items.map((item) => {
                const isActive =
                  item.href === '/'
                    ? pathname === '/'
                    : pathname.startsWith(item.href);
                return (
                  <Link
                    key={`nav-${item.href}`}
                    href={item.href}
                    className={`sidebar-nav-item mb-1 ${isActive ? 'active' : ''}`}
                    title={collapsed ? item.label : undefined}
                    style={{ justifyContent: collapsed ? 'center' : undefined }}
                  >
                    <Icon
                      name={item.icon as Parameters<typeof Icon>[0]['name']}
                      size={20}
                      className="flex-shrink-0"
                    />
                    {!collapsed && (
                      <>
                        <span className="flex-1 truncate">{item.label}</span>
                        {item.badge !== undefined && item.badge > 0 && (
                          <span
                            className="text-xs font-bold px-1.5 py-0.5 rounded-full"
                            style={{ background: '#C9A84C', color: '#1B2A4A', minWidth: 20, textAlign: 'center' }}
                          >
                            {item.badge}
                          </span>
                        )}
                      </>
                    )}
                    {collapsed && item.badge !== undefined && item.badge > 0 && (
                      <span
                        className="absolute top-1 right-1 text-xs font-bold w-4 h-4 rounded-full flex items-center justify-center"
                        style={{ background: '#C9A84C', color: '#1B2A4A', fontSize: 10 }}
                      >
                        {item.badge}
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>
          );
        })}
      </nav>

      {/* Bottom */}
      <div
        className="px-2 py-4"
        style={{ borderTop: '1px solid rgba(201, 168, 76, 0.2)' }}
      >
        {/* Dark mode toggle */}
        <button
          onClick={toggleTheme}
          className="sidebar-nav-item mb-1 w-full text-left"
          title={collapsed ? (isDark ? 'Light Mode' : 'Dark Mode') : undefined}
          style={{ justifyContent: collapsed ? 'center' : undefined }}
        >
          <Icon name={isDark ? 'SunIcon' : 'MoonIcon'} size={18} className="flex-shrink-0" />
          {!collapsed && <span className="truncate">{isDark ? 'Light Mode' : 'Dark Mode'}</span>}
        </button>

        <Link
          href="#"
          className="sidebar-nav-item mb-1"
          title={collapsed ? 'Settings' : undefined}
          style={{ justifyContent: collapsed ? 'center' : undefined }}
        >
          <Icon name="Cog6ToothIcon" size={18} className="flex-shrink-0" />
          {!collapsed && <span className="truncate">Settings</span>}
        </Link>

        <button
          onClick={handleSignOut}
          className="sidebar-nav-item mb-1 w-full text-left"
          title={collapsed ? 'Sign Out' : undefined}
          style={{ justifyContent: collapsed ? 'center' : undefined }}
        >
          <Icon name="ArrowRightOnRectangleIcon" size={18} className="flex-shrink-0" />
          {!collapsed && <span className="truncate">Sign Out</span>}
        </button>

        {/* Toggle button */}
        <button
          onClick={onToggle}
          className="sidebar-nav-item w-full mt-2"
          style={{ justifyContent: collapsed ? 'center' : undefined }}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <Icon
            name={collapsed ? 'ChevronRightIcon' : 'ChevronLeftIcon'}
            size={18}
            className="flex-shrink-0"
          />
          {!collapsed && <span className="truncate text-sm">Collapse</span>}
        </button>
      </div>
    </aside>
  );
}